import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {Dispatch, SetStateAction, useState, useMemo} from 'react';

import {assert} from 'Asserts';
import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import buildFullExperimentPlan from 'procedures/planning/buildFullExperimentPlan';
import {PreconfiguredAnalysisMissingTemplatesWarningBanner} from 'procedures/shared-components/analysis/PreconfiguredAnalysisMissingTemplatesWarningBanner';
import {
  getExperimentUnknownErrorAction,
  onOpenAfterExperimentCreate,
  onSuccessAfterExperimentCreate,
} from 'studies/sidepanel/ExperimentCreationTrayTask';
import {onCreateProcedureRuns} from 'studies/sidepanel/onCreateProcedureRuns';
import {CLINICAL_STUDIES_VALIDATION_CONSTANTS, EXPERIMENT_VALIDATION_CONSTANTS} from 'studies/StudyConstants';
import {BulkCreateExperimentsParam, CreateExperimentsResponse, ProcedureInformation} from 'studies/StudyDAO';
import {serializeExperimentConditionsForNetwork} from 'studies/typed/process-development/ProcessDevelopmentStudyNetworkTranslators';
import {
  Condition,
  useExperimentalInputsContextProperties,
} from 'studies/typed/process-development/useProcessDevelopmentStudyState';
import {
  ExperimentProcedure,
  prepareInitialExperimentalInputs,
  useExperimentProcedure,
} from 'studies/typed/shared/ExperimentalInputs';
import {shouldShowProcedureForStudyCreation} from 'studies/typed/shared/ProcedureDropdown';
import {ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import {InputErrorMessageStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import Modal, {ModalButton} from 'ui-library/modals/Modal';

import {getDefaultConditionNames} from '../shared/conditionUtils';
import {SampleTestPlanProcedureTemplate} from './AssayRunsTable';
import AssayRunsTableProcedureDropdown from './AssayRunsTableProcedureDropdown';

export interface AddAssayRunsModalProps {
  onRequestClose: () => void;
  onRequestSubmit?: () => void;
  studyType: StudyType;
  studyId: string;
  procedureTemplatesToRender: Array<SampleTestPlanProcedureTemplate>;
}

function getButtons(inValidDataMessage: string | null): Array<ModalButton> {
  return [
    {cancel: true, disabled: false, text: 'Cancel'},
    {
      disabled: inValidDataMessage != null,
      tooltipTitle: inValidDataMessage ?? null,
      submit: true,
      text: 'Create',
      style: ButtonStyle.SECONDARY,
      dataTestAttrs: {component: 'AddAssayRunsModal', element: 'submitButton'},
    },
  ];
}

interface AddAssayRunsDesignState {
  /** User-selected procedure id */
  procedureId: string | null;
  /** Setter for {@link procedureId} */
  setProcedureId: Dispatch<SetStateAction<string | null>>;
  /** Number of assay runs */
  numAssayRuns: number | null;
  /** Setter for {@link numAssayRuns} */
  setNumAssayRuns: Dispatch<SetStateAction<number | null>>;
}

function AddAssayRunModalContent(
  props: AddAssayRunsModalProps & {
    designState: AddAssayRunsDesignState;
    numMethods: number | null;
    procedureTemplatesToRender: Array<SampleTestPlanProcedureTemplate>;
  }
): JSX.Element {
  const {designState, numMethods} = props;
  const validationError = validateNumAssayRuns(designState.numAssayRuns, numMethods);
  return props.procedureTemplatesToRender.length === 0 ? (
    // This is a fallback message only. This modal should not be openable if there are no available templates.
    <div>No assays are available for adding to this study.</div>
  ) : (
    <>
      <AssayRunsTableProcedureDropdown
        procedureId={designState.procedureId}
        setProcedureId={designState.setProcedureId}
        onRequestCloseModal={props.onRequestClose}
        requiredField={true}
        dropdownDisabled={false}
        studyId={props.studyId}
        procedureTemplatesToRender={props.procedureTemplatesToRender}
      />
      <TextField
        label="Number of runs"
        placeholder="Number of runs"
        value={designState.numAssayRuns ? String(designState.numAssayRuns) : ''}
        isRequired={true}
        onChange={(e) => {
          designState.setNumAssayRuns(e.target.value.length > 0 ? Number(e.target.value) : null);
        }}
        hasError={validationError != null}
        error={{message: validationError, style: InputErrorMessageStyle.ERROR}}
      />
      <PreconfiguredAnalysisMissingTemplatesWarningBanner
        selectedProcedureId={designState.procedureId}
        studyType={props.studyType}
        procedureOptions={props.procedureTemplatesToRender}
      />
    </>
  );
}

/**
 * Hook to manage the state for designing a procedure-based experiment.
 *
 * @returns a {@link AddAssayRunsDesignState} object.
 */
function useAssayRunDesignState(): AddAssayRunsDesignState {
  const [procedureId, setProcedureId] = useState<string | null>(null);
  const [numAssayRuns, setNumAssayRuns] = useState<number>(1);
  return {procedureId, setProcedureId, numAssayRuns, setNumAssayRuns};
}

/**
 * Simplified version of {@link ProcedureRunDesignEditor}'s addConditions function that only creates conditions,
 * instead of appending to an existing list of conditions.
 */
function createConditions(
  numConditions: number,
  numReplicates: number,
  procedureType: ProcedureType
): Array<Condition> {
  const conditionNames = getDefaultConditionNames(numConditions, procedureType);

  return Array.from({length: numConditions}, (_, index: number) => ({
    numReplicates,
    name: conditionNames[index],
    validationState: {
      isDuplicateName: false,
      isNameLengthOverLimit:
        conditionNames[index].length > EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_NAME_MAX_LENGTH,
      isDescLengthOverLimit: false,
    },
  }));
}

interface BulkCreateExperimentsParamSuccess {
  type: 'SUCCESS';
  creates: BulkCreateExperimentsParam['creates'];
}

interface BulkCreateExperimentsParamPending {
  type: 'PENDING';
  hasProcedure: boolean;
  hasNumAssayRuns: boolean;
}

/**
 * Simplifies functionality from {@link useExperimentDesignState} to create experiment parameters for assay runs.
 *
 */
function useCreateExperimentParamsForAssayRuns(
  studyId: string,
  assayTemplateName: string | null,
  procedure: ExperimentProcedure | null,
  numAssayRuns: number | null
): BulkCreateExperimentsParamSuccess | BulkCreateExperimentsParamPending {
  const numConditions = 1;
  const numReplicates = 1;
  const procedureType = ProcedureType.ASSAY;
  const experimentalInputs =
    procedure != null ? prepareInitialExperimentalInputs(numConditions, procedure?.methods) : null;
  const conditions = createConditions(numConditions, numReplicates, procedureType);

  const replicatesPerCondition = conditions.map((condition) => condition.numReplicates);
  const namesPerCondition = conditions.map((condition) => condition.name);
  const descriptionsPerCondition = conditions.map((condition) => condition.description);

  const context = useExperimentalInputsContextProperties(
    conditions,
    experimentalInputs,
    noop,
    procedure?.procedureFlowchart ?? null,
    procedure?.procedureType ?? null,
    false
  );

  const experimentPlan =
    experimentalInputs != null && context?.evaluatedValues != null
      ? buildFullExperimentPlan({
          replicatesPerCondition,
          namesPerCondition,
          descriptionsPerCondition,
          numConditions: replicatesPerCondition.length,
          experimentalInputs,
          evaluatedValues: context.evaluatedValues,
        })
      : null;

  if (
    procedure == null ||
    experimentPlan == null ||
    numAssayRuns == null ||
    !Number.isInteger(numAssayRuns) ||
    numAssayRuns < 1
  ) {
    return {
      type: 'PENDING',
      hasProcedure: procedure != null,
      hasNumAssayRuns: numAssayRuns != null && Number.isInteger(numAssayRuns) && numAssayRuns >= 1,
    };
  }

  const experimentParams: ProcedureInformation = {
    templateProcedureFlowchartVersionId: procedure.procedureFlowchartVersionId,
    workflowFlowchartConfigVersionId: procedure.workflowFlowchartConfigVersionId,
    experimentConditions: serializeExperimentConditionsForNetwork(experimentPlan),
    description: '',
    confirmationMethodByParamVersionId: experimentPlan.confirmationMethodByParamVersionId,
  };

  return {type: 'SUCCESS', creates: Array(numAssayRuns).fill(experimentParams)};
}

function validateNumAssayRuns(numAssayRunsToAdd: number | null, numMethods: number | null): string | null {
  if (numAssayRunsToAdd == null) {
    return 'Number of runs field is required';
  }
  if (!Number.isInteger(numAssayRunsToAdd)) {
    return 'Number of runs must be an integer';
  }
  if (Number(numAssayRunsToAdd) < 1) {
    return 'Number of runs must be greater than 0';
  }
  // holds the number of assay runs that can be added per submission of the modal
  const maxNumAssayRunsPerModalRun = numMethods
    ? CLINICAL_STUDIES_VALIDATION_CONSTANTS.MAX_NUM_ASSAY_RUNS_PER_MODAL_RUN / numMethods
    : CLINICAL_STUDIES_VALIDATION_CONSTANTS.MAX_NUM_ASSAY_RUNS_PER_MODAL_RUN;
  if (Number(numAssayRunsToAdd) > maxNumAssayRunsPerModalRun) {
    return `Number of runs cannot be greater than ${maxNumAssayRunsPerModalRun}`;
  }
  return null;
}

/*
 * True if provided data is complete, valid, and should allow submitting the modal.
 * false otherwise.
 */
function validateData(
  procedureId: string | null,
  createExperimentsParams: BulkCreateExperimentsParamSuccess | BulkCreateExperimentsParamPending,
  availableProcedureTemplates: Array<SampleTestPlanProcedureTemplate>
): string | null {
  if (availableProcedureTemplates.length === 0) {
    return 'No assays are available for adding to this study.';
  }
  if (procedureId == null) {
    return 'Please select an assay or wait for validation to complete';
  }
  if (createExperimentsParams.type === 'PENDING') {
    if (!createExperimentsParams.hasProcedure) {
      return 'Assay is loading. Please wait and try again.';
    } else if (!createExperimentsParams.hasNumAssayRuns) {
      return 'Number of runs field is required';
    }
  }
  return null;
}

export default function AddAssayRunsModal(props: AddAssayRunsModalProps): JSX.Element {
  const [isBackground, setIsBackground] = useState(false);
  const designState = useAssayRunDesignState();
  // Get the procedure params for the experiment procedure hook and procedure name for the experiment creation
  const {procedureParams, isPow} = useMemo(() => {
    const procedureTemplate = props.procedureTemplatesToRender.find(
      (template) => template.id === designState.procedureId
    );
    return {
      procedureParams: {
        procedureId: designState.procedureId,
        procedureName: procedureTemplate?.name ?? null,
        procedureFlowchartVersionId: procedureTemplate?.procedureFlowchartVersionId ?? null,
      },
      isPow: procedureTemplate?.publishedProcedureFlowchartConfigVersionProxy != null,
    };
  }, [designState.procedureId, props.procedureTemplatesToRender]);
  // Only pass procedureFlowchartVersionId for legacy procedures.
  // For PoW procedures, let useExperimentProcedure fetch the published version from the procedure.
  const {procedure} = useExperimentProcedure(
    procedureParams.procedureId && procedureParams.procedureFlowchartVersionId && !isPow
      ? {
          procedureId: procedureParams.procedureId,
          procedureFlowchartVersionId: procedureParams.procedureFlowchartVersionId,
        }
      : procedureParams.procedureId
        ? {
            procedureId: procedureParams.procedureId,
          }
        : null,
    props.studyType
  );

  const createExperimentsParams = useCreateExperimentParamsForAssayRuns(
    props.studyId,
    procedureParams.procedureName,
    procedure,
    designState.numAssayRuns
  );
  const procedureTemplatesToRender = props.procedureTemplatesToRender.filter((p) =>
    shouldShowProcedureForStudyCreation(p)
  );

  const validateDataError = validateData(
    designState.procedureId,
    createExperimentsParams,
    procedureTemplatesToRender
  );
  const numAssayRunsValidationError = validateNumAssayRuns(
    designState.numAssayRuns,
    procedure?.methods.length ?? null
  );
  const finalValidationError = validateDataError ?? numAssayRunsValidationError;

  const handleSubmit = async (): Promise<void> => {
    assert(createExperimentsParams.type === 'SUCCESS', 'Expected experiment parameters to be defined.');
    await onCreateProcedureRuns(props.studyId, props.studyType, createExperimentsParams.creates, {
      onSuccess: async (response: CreateExperimentsResponse) => {
        await onSuccessAfterExperimentCreate(props.studyId, response, props.studyType);
        props.onRequestClose();
      },
      onOpen: () => {
        onOpenAfterExperimentCreate(props.studyId);
      },
      onBackground: (isBackground: boolean) => {
        setIsBackground(isBackground);
      },
      onUnknownError: getExperimentUnknownErrorAction(props.studyId, props.studyType),
    });
  };

  if (isBackground) {
    return <></>;
  }

  return (
    <Modal
      title={
        <div className={css(styles.titleWrapper)}>
          <span>Create assay run(s)</span>
        </div>
      }
      onRequestClose={() => {
        props.onRequestClose();
      }}
      onRequestSubmit={handleSubmit}
      buttons={getButtons(finalValidationError)}
      closeableOnEscapeKey={false}
      closeableOnBackgroundClick={false}
    >
      <AddAssayRunModalContent
        {...props}
        procedureTemplatesToRender={procedureTemplatesToRender}
        designState={designState}
        numMethods={procedure?.methods.length ?? null}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  titleWrapper: {display: 'flex', flexWrap: 'wrap', columnGap: '50px', rowGap: 10},
  errorText: {color: Colors.RED, marginTop: -12},
});
